import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Canal } from '../types';
import { canaisApi } from '../services/api';
import { Plus, Edit, Trash2, Radio } from 'lucide-react';

interface CanalForm { descricao: string; isDefault: boolean; ativo: boolean; }

const emptyForm: CanalForm = { descricao: '', isDefault: false, ativo: true };

export function CanaisPage() {
  const { success, error } = useToast();
  const [canais, setCanais] = useState<Canal[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Canal | null>(null);
  const [form, setForm] = useState<CanalForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    try { const r = await canaisApi.getAll(); if (r.success && r.data) setCanais(r.data); }
    catch (err) {
      console.error('Failed to load canais:', err);
      error('Erro ao carregar canais');
    }
    finally { setLoading(false); }
  }, [error]);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await canaisApi.getAll();
        if (active && r.success && r.data) setCanais(r.data);
      } catch (err) {
        if (active) {
          console.error('Failed to load canais:', err);
          error('Erro ao carregar canais');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [error]);

  const openNovo = () => { setForm(emptyForm); setEditando(null); setFormError(''); setModalOpen(true); };
  const openEditar = (c: Canal) => { setForm({ descricao: c.descricao, isDefault: c.isDefault, ativo: c.ativo }); setEditando(c); setFormError(''); setModalOpen(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim()) { setFormError('Descrição obrigatória'); return; }
    setSaving(true);
    try {
      if (editando) {
        const r = await canaisApi.update(editando.id, { descricao: form.descricao.trim(), isDefault: form.isDefault, ativo: form.ativo, versao: editando.versao });
        if (r.success){ success('Canal atualizado'); setModalOpen(false); load(); } else setFormError(r.message||'Erro');
      } else {
        const r = await canaisApi.create({ descricao: form.descricao.trim(), isDefault: form.isDefault, ativo: form.ativo });
        if (r.success){ success('Canal criado'); setModalOpen(false); load(); } else setFormError(r.message||'Erro');
      }
    } catch (e:any){ setFormError(e.message ?? 'Erro'); }
    finally { setSaving(false); }
  };
  const handleEliminar = async (c: Canal) => {
    if (!confirm(`Eliminar "${c.descricao}"?`)) return;
    try { const r = await canaisApi.delete(c.id); if (r.success){ success('Eliminado'); load(); } else error(r.message||'Erro'); }
    catch (err) {
      console.error('Failed to delete canal:', err);
      error('Erro ao eliminar');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header"><h1 className="page-title">Canais</h1><button className="btn btn-primary" onClick={openNovo}><Plus className="w-4 h-4"/> Novo Canal</button></div>
      <div className="card">
        {loading ? <div className="page-loading"><div className="spinner"></div><p>A carregar...</p></div> : (
          <div className="table-container"><table className="table"><thead><tr><th>Descrição</th><th>Default</th><th>Ativo</th><th>Ações</th></tr></thead>
            <tbody>{canais.map(c=>(
              <tr key={c.id}><td>{c.descricao} {c.isDefault && <span className="badge badge-info ml-2">Default</span>}</td>
                <td>{c.isDefault ? 'Sim' : 'Não'}</td><td><span className={`badge ${c.ativo?'badge-success':'badge-danger'}`}>{c.ativo?'Sim':'Não'}</span></td>
                <td><div className="table-actions"><button className="btn btn-ghost btn-sm btn-icon" onClick={()=>openEditar(c)}><Edit className="w-4 h-4"/></button><button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={()=>handleEliminar(c)}><Trash2 className="w-4 h-4"/></button></div></td>
              </tr>))}</tbody>
          </table></div>
        )}
        {!loading && canais.length===0 && <div className="empty-state"><Radio className="w-12 h-12 text-gray-300"/><h3>Nenhum canal</h3><button className="btn btn-primary mt-4" onClick={openNovo}><Plus className="w-4 h-4"/> Novo</button></div>}
      </div>
      <Modal title={editando?'Editar Canal':'Novo Canal'} open={modalOpen} onClose={()=>setModalOpen(false)} footer={<><button className="btn btn-secondary" onClick={()=>setModalOpen(false)}>Cancelar</button><button type="submit" form="canal-form" className="btn btn-primary" disabled={saving}>{saving?'A guardar...': editando?'Atualizar':'Criar'}</button></>}>
        <form id="canal-form" onSubmit={handleSubmit} noValidate>
          {formError && <div className="form-error">{formError}</div>}
          <div className="form-group"><label className="label">Descrição *</label><input className="input" value={form.descricao} onChange={e=>setForm(p=>({...p, descricao:e.target.value}))} placeholder="Ex: Telefone, Walk-in, TheFork" required /></div>
          <div className="form-row">
            <div className="form-group form-checkbox"><label className="checkbox-label"><input type="checkbox" checked={form.isDefault} onChange={e=>setForm(p=>({...p, isDefault:e.target.checked}))}/><span>Default</span></label></div>
            <div className="form-group form-checkbox"><label className="checkbox-label"><input type="checkbox" checked={form.ativo} onChange={e=>setForm(p=>({...p, ativo:e.target.checked}))}/><span>Ativo</span></label></div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
